import { jsx as _jsx } from "react/jsx-runtime";
import { AnimatePresence, motion, useIsPresent } from 'framer-motion';
import mapboxgl from 'mapbox-gl';
import { useCallback, useEffect, useMemo } from 'react';
import slugify from 'slugify';
import { isTripMarker } from '../data/helpers';
import useMapMarkers from '../hooks/useMapMarkers';
import useRouter from '../hooks/useRouter';
import useSessionState from '../hooks/useSessionState';
import CardWrapper from './CardWrapper';
import IntroCard from './IntroCard';
import RenderContentCard from './RenderContentCard';
import RegionalMapFilters from './RegionalMapFilters';
const HOME_PATH = 'layer-home';
const TRANSITION_VW = 100;
const TRANSITION_SEC = 0.4;
const variants = {
    initial: (direction) => ({
        x: Math.sign(direction) * TRANSITION_VW + 'vw',
        opacity: 0,
    }),
    animate: {
        x: '0',
        opacity: 1,
        transition: {
            duration: TRANSITION_SEC,
            ease: 'easeOut',
        },
    },
    exit: (direction) => ({
        x: Math.sign(direction) * TRANSITION_VW + 'vw',
        opacity: 0,
        transition: {
            duration: TRANSITION_SEC,
            ease: 'easeOut',
        },
    }),
};
const MapLayer = ({ map, route: { path, meta }, direction, getBounds, includeMajorCities, }) => {
    const { layer, variant, homeCard = true, showFilters = false } = meta;
    /**
     * Routing
     */
    // Namespaces all marker routes to the parent section route
    const prefixWithPath = useCallback((str) => {
        return slugify(`${path}-${str}`).toLowerCase();
    }, [path]);
    // Set up a special route for the home card
    const homeRoute = useMemo(() => ({
        path: prefixWithPath(HOME_PATH),
        meta: null,
    }), [prefixWithPath]);
    // Convert marker data to routes
    const routes = useMemo(() => {
        const markerRoutes = layer.posts.map((marker) => ({
            path: prefixWithPath(marker.title),
            meta: {
                marker,
                variant: variant
            },
        }));
        return [homeRoute, ...markerRoutes];
    }, [layer, variant, homeRoute, prefixWithPath]);
    // Initialise router
    const nav = useRouter(routes);
    /**
     * Map markers
     */
    // Set up markers
    const isPresent = useIsPresent();
    const { bounds: mapBounds, update: updateMarkers } = useMapMarkers({
        map,
        routes,
        shouldRender: isPresent,
        fadeDuration: TRANSITION_SEC,
        setRoute: nav.setRoute,
        includeMajorCities,
    });
    // Re-render markers when route changes
    useEffect(() => {
        if (isPresent)
            updateMarkers(nav.route.path);
    }, [nav, updateMarkers, isPresent]);
    /**
     * Home card logic
     */
    // Home card is only visible until closed for the current session
    const [homeDismissed, setHomeDismissed] = useSessionState(prefixWithPath('home-dismissed'), false);
    const showHome = nav.route.path === homeRoute.path && homeCard && !homeDismissed;
    const returnHome = () => nav.setRoute(homeRoute);
    /**
     * Camera controls
     */
    // Fits all markers to viewport
    const fitBounds = useCallback((options) => {
        const bounds = options?.bounds ?? mapBounds;
        const duration = options?.duration ?? 750;
        if (map.current && !bounds.isEmpty()) {
            map.current.fitBounds(bounds, { duration, padding: getBounds() });
        }
    }, [map, mapBounds, getBounds]);
    // Re-fit bounds when they change (as a result of changing sections)
    useEffect(() => {
        fitBounds();
    }, [fitBounds]);
    // Centers the camera on a specific location
    const targetLngLat = useCallback((lngLat, duration = 750) => {
        const { left, top, right, bottom } = getBounds('center');
        map.current?.flyTo({
            center: lngLat,
            offset: [(left - right) / 2, (top - bottom) / 2],
            duration,
        });
    }, [map, getBounds]);
    // Updates the camera based on the current route
    const updateCamera = useCallback(() => {
        const { path, meta } = nav.route;
        if (path === homeRoute.path) {
            fitBounds();
        }
        else if (meta) {
            if (isTripMarker(meta.marker)) {
                // Fit this marker, and the start & end of the trip route
                const tripBounds = new mapboxgl.LngLatBounds();
                tripBounds.extend(meta.marker.lnglat);
                tripBounds.extend(meta.marker.route.start);
                tripBounds.extend(meta.marker.route.end);
                fitBounds({ bounds: tripBounds });
            }
            else {
                // Just target the marker
                targetLngLat(meta.marker.lnglat);
            }
        }
    }, [nav, homeRoute, fitBounds, targetLngLat]);
    // Only updates the camera if the home card has been closed
    const updateCameraIfHomeDismissed = () => {
        if (nav.route.path === homeRoute.path && homeDismissed)
            updateCamera();
    };
    return (_jsx(motion.div, { className: "min-h-0 z-10 laptop:order-1", variants: variants, custom: direction, initial: "initial", animate: "animate", exit: "exit", children: _jsx(AnimatePresence, { exitBeforeEnter: true, initial: false, onExitComplete: updateCameraIfHomeDismissed, children: showHome ? (_jsx(CardWrapper, { onEnter: updateCamera, children: _jsx(IntroCard, { title: layer.title, body: layer.intro, icon: layer.icon, onClose: () => setHomeDismissed(true) }) }, nav.route.path)) : nav.route.meta ? (_jsx(RenderContentCard, { data: nav.route.meta.marker, onEnter: updateCamera, onClose: returnHome }, nav.route.path)) : showFilters ? (_jsx(RegionalMapFilters, { onEnter: updateCamera })) : null }) }));
};
export default MapLayer;
